/**
 * <notify-me-form> — back-in-stock capture inside the notify drawer.
 *
 * - Keeps the size dropdown synced to the sold-out variant selected on the product page.
 * - On submit, posts {email, variant_id, ...} to the Apps Script endpoint (fire-and-forget,
 *   no CORS preflight), then swaps the form for a confirmation panel and auto-closes the drawer.
 * - Resets back to the form whenever the drawer is re-opened.
 */
const AUTO_CLOSE_MS = 2500;

class NotifyMeForm extends HTMLElement {
  #controller = null;
  #openObserver = null;
  #closeTimer = null;
  #submitting = false;

  connectedCallback() {
    this.form = this.querySelector('[data-notify-form]');
    this.select = this.querySelector('[data-notify-size]');
    this.priceEl = this.querySelector('[data-notify-price]');
    this.errorEl = this.querySelector('[data-notify-error]');
    this.successEl = this.querySelector('[data-notify-success]');
    this.submitButton = this.querySelector('[type="submit"]');
    this.drawer = this.closest('x-drawer');

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.form?.addEventListener('submit', this.#handleSubmit.bind(this), { signal });
    this.select?.addEventListener('change', this.#syncPrice.bind(this), { signal });
    document.addEventListener('variant:change', this.#onVariantChange.bind(this), { signal });

    // Reset to a fresh form each time the drawer is (re-)opened.
    if (this.drawer) {
      this.#openObserver = new MutationObserver(() => {
        if (this.drawer.hasAttribute('open')) this.#reset();
      });
      this.#openObserver.observe(this.drawer, { attributes: true, attributeFilter: ['open'] });
    }

    this.#reset();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#openObserver?.disconnect();
    if (this.#closeTimer) clearTimeout(this.#closeTimer);
  }

  #onVariantChange(event) {
    const variant = event.detail?.variant;
    if (!variant || !this.select) return;
    const option = this.select.querySelector(`option[value="${variant.id}"]`);
    if (option && !option.disabled) {
      this.select.value = String(variant.id);
      this.#syncPrice();
    }
  }

  #ensureSelection() {
    if (!this.select) return;
    const selected = this.select.selectedOptions[0];
    if (!selected || selected.disabled) {
      const firstSelectable = Array.from(this.select.options).find((option) => !option.disabled);
      if (firstSelectable) this.select.value = firstSelectable.value;
    }
  }

  #syncPrice() {
    const option = this.select?.selectedOptions[0];
    if (this.priceEl && option?.dataset.price) {
      this.priceEl.textContent = option.dataset.price;
    }
  }

  async #handleSubmit(event) {
    event.preventDefault();
    if (this.#submitting) return;
    const endpoint = this.dataset.endpoint;
    if (!endpoint) return;
    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    this.#submitting = true;
    this.submitButton?.setAttribute('aria-busy', 'true');
    if (this.errorEl) this.errorEl.hidden = true;

    const payload = this.#serialize();

    try {
      await fetch(endpoint, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(payload),
        signal: this.#controller?.signal,
      });
      this.#showSuccess();
      this.dispatchEvent(
        new CustomEvent('notify-me:submitted', {
          bubbles: true,
          detail: { email: payload.email, variantId: payload.variant_id },
        })
      );
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (this.errorEl) this.errorEl.hidden = false;
    } finally {
      this.submitButton?.removeAttribute('aria-busy');
      this.#submitting = false;
    }
  }

  #showSuccess() {
    if (this.form) this.form.hidden = true;
    if (this.successEl) {
      this.successEl.hidden = false;
      this.successEl.focus();
    }
    this.#closeTimer = setTimeout(() => this.drawer?.hide(), AUTO_CLOSE_MS);
  }

  #reset() {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
    if (this.form) this.form.hidden = false;
    if (this.successEl) this.successEl.hidden = true;
    if (this.errorEl) this.errorEl.hidden = true;
    this.submitButton?.removeAttribute('aria-busy');
    this.#ensureSelection();
    this.#syncPrice();
  }

  #serialize() {
    const data = {};
    for (const el of this.form.elements) {
      if (el.name) data[el.name] = el.value;
    }
    const option = this.select?.selectedOptions[0];
    if (option) data.variant_title = option.dataset.title || '';
    return data;
  }
}

if (!customElements.get('notify-me-form')) {
  customElements.define('notify-me-form', NotifyMeForm);
}
